import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
	uploadStudentApplicationDocument,
	downloadStudentApplicationDocument,
} from 'api/applicationDocuments'
import { useSafeErrorBanner } from 'shared/components/ErrorBanner/useSafeErrorBanner'

const MAX_BYTES = 3 * 1024 * 1024
const ACCEPT_EXT = new Set(['pdf', 'doc', 'docx'])

export function validateUploadStudentDocumentFile(file) {
	if (!file) return 'No file selected.'
	const ext = file.name.split('.').pop()?.toLowerCase() || ''
	if (!ACCEPT_EXT.has(ext)) {
		return 'Accepted file types: PDF, DOC, DOCX only.'
	}
	if (file.size > MAX_BYTES) {
		return 'FILE SIZE MORE THAN 3 MB'
	}
	return null
}

export function useUploadStudentDocumentModal({
	isOpen,
	studentId,
	requirementId,
	onUploaded,
	onRequestClose,
	onPreviewBlob,
}) {
	const { showErrorSafe } = useSafeErrorBanner()
	const inputRef = useRef(null)
	const [dragging, setDragging] = useState(false)
	const [phase, setPhase] = useState('idle')
	const [errorMessage, setErrorMessage] = useState('')
	const [uploadedFileId, setUploadedFileId] = useState(null)

	const resetLocal = useCallback(() => {
		setPhase('idle')
		setErrorMessage('')
		setUploadedFileId(null)
		setDragging(false)
	}, [])

	useEffect(() => {
		if (!isOpen) resetLocal()
	}, [isOpen, resetLocal])

	const runUpload = useCallback(
		async file => {
			const sid = String(studentId || '').trim()
			const rid = String(requirementId || '').trim()
			if (!sid || !rid) {
				setPhase('error')
				setErrorMessage('Missing student or document.')
				return
			}
			const clientErr = validateUploadStudentDocumentFile(file)
			if (clientErr) {
				setPhase('error')
				setErrorMessage(clientErr)
				return
			}
			setPhase('uploading')
			setErrorMessage('')
			try {
				const data = await uploadStudentApplicationDocument(sid, rid, file)
				const fid = data?.id ?? data?.file_id ?? data?.fileId
				setUploadedFileId(fid || null)
				setPhase('success')
				await onUploaded?.()
			} catch (e) {
				setPhase('error')
				setErrorMessage(
					e?.response?.data?.error ||
						e?.message ||
						'Upload failed. Please try again.',
				)
			}
		},
		[studentId, requirementId, onUploaded],
	)

	const onInputChange = useCallback(
		e => {
			const f = e.target.files?.[0]
			e.target.value = ''
			if (f) runUpload(f)
		},
		[runUpload],
	)

	const onDrop = useCallback(
		e => {
			e.preventDefault()
			e.stopPropagation()
			setDragging(false)
			const f = e.dataTransfer?.files?.[0]
			if (f) runUpload(f)
		},
		[runUpload],
	)

	const onDragOver = useCallback(e => {
		e.preventDefault()
		e.dataTransfer.dropEffect = 'copy'
	}, [])

	const onDragEnter = useCallback(e => {
		e.preventDefault()
		setDragging(true)
	}, [])

	const onDragLeave = useCallback(e => {
		e.preventDefault()
		setDragging(false)
	}, [])

	const handleSeeDocument = useCallback(async () => {
		if (!studentId || !uploadedFileId) return
		try {
			const blob = await downloadStudentApplicationDocument(
				studentId,
				uploadedFileId,
			)
			const url = URL.createObjectURL(blob)
			if (onPreviewBlob) {
				onPreviewBlob({ url, title: 'Document' })
			} else {
				window.open(url, '_blank', 'noopener,noreferrer')
				setTimeout(() => URL.revokeObjectURL(url), 60_000)
			}
		} catch (e) {
			showErrorSafe(
				e?.response?.data?.error ||
					e?.message ||
					'Could not open the document.',
			)
		}
	}, [studentId, uploadedFileId, onPreviewBlob, showErrorSafe])

	const openFilePicker = useCallback(() => {
		requestAnimationFrame(() => {
			inputRef.current?.click()
		})
	}, [])

	const tryAgain = useCallback(() => {
		resetLocal()
		openFilePicker()
	}, [resetLocal, openFilePicker])

	const close = useCallback(() => {
		onRequestClose?.()
	}, [onRequestClose])

	const onDropzoneClick = useCallback(() => {
		if (phase !== 'uploading') inputRef.current?.click()
	}, [phase])

	const onDropzoneKeyDown = useCallback(
		e => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault()
				if (phase !== 'uploading') inputRef.current?.click()
			}
		},
		[phase],
	)

	const onUploadButtonClick = useCallback(e => {
		e.stopPropagation()
		inputRef.current?.click()
	}, [])

	const showDropzone = phase !== 'success'
	const showSuccess = phase === 'success'
	const showError = phase === 'error'

	const footerButtons = useMemo(() => {
		return showDropzone && !showError
			? null
			: [
					{
						id: 'done',
						text: 'Done',
						variant: 'primary',
						onClick: close,
						disabled: showError,
					},
				]
	}, [showDropzone, showError, close])

	const dropzoneClassNames = useMemo(
		() =>
			[dragging ? 'dragging' : '', phase === 'uploading' ? 'disabled' : ''].filter(
				Boolean,
			),
		[dragging, phase],
	)

	return {
		inputRef,
		phase,
		errorMessage,
		showDropzone,
		showSuccess,
		showError,
		footerButtons,
		close,
		tryAgain,
		handleSeeDocument,
		dropzoneClassNames,
		onInputChange,
		onDrop,
		onDragOver,
		onDragEnter,
		onDragLeave,
		onDropzoneClick,
		onDropzoneKeyDown,
		onUploadButtonClick,
		inputDisabled: phase === 'uploading',
		uploadButtonLabel: phase === 'uploading' ? 'Uploading…' : 'Upload from Computer',
	}
}
